import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Heart, Bell, Menu, X,
  LogOut, Settings, Package, Award, Stethoscope, ChevronDown,
  FileText, Users, Gift, RefreshCw, MessageCircle, TrendingUp,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { useNotificationStore } from '@/store/notificationStore';
import { SearchBar } from '@/components/common/SearchBar';
import { cn } from '@/utils';
import { getTier } from '@/utils/loyaltyTiers';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/services/api';

export const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuthStore();
  const { itemCount } = useCartStore();
  const { unreadCount } = useNotificationStore();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  const cartCount = itemCount();
  const notifCount = unreadCount();

  return (
    <header className={cn(
      'sticky top-0 z-50 transition-all duration-300',
      scrolled
        ? 'bg-cream/90 backdrop-blur-md shadow-soft border-b border-rule/60'
        : 'bg-cream border-b border-rule/40'
    )}>
      {/* Topbar */}
      <div className="bg-primary-600 text-white/90 text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          <span className="hidden sm:block">{t('topbar')}</span>
          <span className="sm:hidden text-center w-full">Free delivery above ₹500 · Nadiad, Gujarat</span>
          <button
            onClick={() => setLanguage(language === 'en' ? 'gu' : 'en')}
            className="shrink-0 text-white/80 hover:text-white border border-white/30 hover:border-white/60 text-[11px] font-semibold px-2.5 py-0.5 rounded-pill transition-all"
          >
            {language === 'en' ? 'ગુ' : 'EN'}
          </button>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center gap-4">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <div className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center shadow-soft group-hover:bg-primary-600 transition-colors">
              <Stethoscope className="h-4.5 w-4.5 text-white" />
            </div>
            <div className="hidden sm:block">
              <p className="font-display font-semibold text-primary-500 text-lg leading-none tracking-tight">Milan Medical</p>
              <p className="text-[10px] text-muted leading-tight tracking-[0.06em]">Pharmacy & Health Store</p>
            </div>
          </Link>

          {/* Search */}
          <div className="flex-1 hidden md:block">
            <SearchBar />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-0.5 ml-auto">

            {isAuthenticated && (
              <Link to="/wishlist" className="p-2.5 text-muted hover:text-primary-500 hover:bg-primary-50/60 rounded-md transition-all" title="Wishlist">
                <Heart className="h-5 w-5" />
              </Link>
            )}

            {isAuthenticated && (
              <div className="relative">
                <button onClick={() => navigate('/notifications')} className="p-2.5 text-muted hover:text-primary-500 hover:bg-primary-50/60 rounded-md transition-all">
                  <Bell className="h-5 w-5" />
                  {notifCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-accent-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                      {notifCount > 9 ? '9+' : notifCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            <Link to="/cart" className="relative p-2.5 text-muted hover:text-primary-500 hover:bg-primary-50/60 rounded-md transition-all">
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-primary-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Link>

            {isAuthenticated ? (
              <div className="relative ml-1">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-primary-50/60 transition-all text-sm font-medium text-ink"
                >
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs font-display">
                    {user?.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden lg:block max-w-[90px] truncate text-ink font-medium">{user?.name.split(' ')[0]}</span>
                  <ChevronDown className="h-3 w-3 text-muted" />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-cream rounded-lg shadow-card-hover border border-rule/80 z-50 overflow-hidden animate-fade-up">
                      <div className="px-4 py-3.5 border-b border-rule/60 bg-cream-2">
                        <p className="font-display font-semibold text-ink text-sm">{user?.name}</p>
                        <p className="text-xs text-muted truncate">{user?.email}</p>
                        {user?.loyaltyPoints !== undefined && (() => {
                          const tier = getTier(user.loyaltyPoints);
                          return (
                            <div className="flex items-center gap-2 mt-1.5">
                              <p className="text-xs text-gold font-semibold">🍃 {user.loyaltyPoints} leaves</p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${tier.bg} ${tier.color}`}>
                                {tier.badge} {tier.name}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="py-1">
                        <MenuItem icon={Package}      label={t('myOrders')}      to="/orders"              onClick={() => setUserMenuOpen(false)} />
                        <MenuItem icon={FileText}     label={t('expenseVault')}  to="/expenses"            onClick={() => setUserMenuOpen(false)} />
                        <MenuItem icon={Users}        label={t('familyCabinet')} to="/family"              onClick={() => setUserMenuOpen(false)} />
                        <MenuItem icon={Award}        label={t('loyaltyPoints')} to="/profile/loyalty"     onClick={() => setUserMenuOpen(false)} />
                        <MenuItem icon={Gift}         label={t('referEarn')}     to="/referral"            onClick={() => setUserMenuOpen(false)} />
                        <MenuItem icon={RefreshCw}    label={t('subscriptions')} to="/subscriptions"       onClick={() => setUserMenuOpen(false)} />
                        <MenuItem icon={MessageCircle} label={t('whatsappAlerts')} to="/whatsapp-settings" onClick={() => setUserMenuOpen(false)} />
                        {user?.role === 'ADMIN' && (
                          <MenuItem icon={Settings}   label={t('adminDashboard')} to="/admin"             onClick={() => setUserMenuOpen(false)} />
                        )}
                        {user?.role === 'ADMIN' && (
                          <MenuItem icon={TrendingUp} label={t('ownerAnalytics')} to="/owner-analytics"   onClick={() => setUserMenuOpen(false)} />
                        )}
                        {(user?.role === 'PHARMACIST' || user?.role === 'ADMIN') && (
                          <MenuItem icon={Stethoscope} label={t('pharmacistPanel')} to="/pharmacist"      onClick={() => setUserMenuOpen(false)} />
                        )}
                        <div className="border-t border-rule/60 mt-1 pt-1">
                          <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-error hover:bg-red-50 transition-colors">
                            <LogOut className="h-4 w-4" /> {t('signOut')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 ml-1">
                <Link to="/login" className="hidden sm:block text-sm font-semibold text-primary-500 hover:text-primary-600 px-3 py-2 transition-colors">
                  {t('login')}
                </Link>
                <Link to="/register" className="text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-white px-5 py-2.5 rounded-pill transition-all shadow-soft">
                  {t('signUp')}
                </Link>
              </div>
            )}

            <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2.5 text-muted hover:bg-cream-3 rounded-md ml-1 transition-all">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className={cn('md:hidden mt-3', !mobileOpen && 'hidden')}>
          <SearchBar onClose={() => setMobileOpen(false)} mobile />
        </div>
      </div>

      {/* Category nav */}
      <nav className="border-t border-rule/60 bg-cream-2/60">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-none py-2">
            {NAV_CATEGORIES.map(cat => (
              <Link
                key={cat.slug}
                to={`/categories/${cat.slug}`}
                className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-muted hover:text-primary-600 hover:bg-primary-50/60 rounded-md transition-all"
              >
                {cat.icon} {t(cat.key)}
              </Link>
            ))}
            <div className="w-px h-3.5 bg-rule mx-1.5 shrink-0" />
            <Link to="/symptoms"    className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-muted hover:text-primary-600 hover:bg-primary-50/60 rounded-md transition-all">🤒 {t('bySymptom')}</Link>
            <Link to="/drug-checker" className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-muted hover:text-primary-600 hover:bg-primary-50/60 rounded-md transition-all">🔍 {t('drugChecker')}</Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

const MenuItem = ({ icon: Icon, label, to, onClick }: { icon: React.ElementType; label: string; to: string; onClick: () => void }) => (
  <Link to={to} onClick={onClick} className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink hover:bg-primary-50/60 hover:text-primary-700 transition-colors">
    <Icon className="h-4 w-4 text-sage shrink-0" /> {label}
  </Link>
);

const NAV_CATEGORIES = [
  { key: 'medicines'   as const, slug: 'medicines',          icon: '💊' },
  { key: 'vitamins'    as const, slug: 'vitamins-supplements', icon: '🌿' },
  { key: 'skincare'    as const, slug: 'skincare-beauty',    icon: '✨' },
  { key: 'personalCare' as const, slug: 'personal-care',    icon: '🧴' },
  { key: 'babyChild'   as const, slug: 'baby-child',         icon: '👶' },
  { key: 'devices'     as const, slug: 'medical-devices',    icon: '🩺' },
  { key: 'ayurvedic'   as const, slug: 'ayurvedic-herbal',   icon: '🌱' },
  { key: 'wellness'    as const, slug: 'sexual-wellness',    icon: '❤️' },
];
